import {
  CallbackProperty,
  Cartesian3,
  defined,
  PolygonHierarchy,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "@cesium/engine";
import MeasurementArea from "../measurement/MeasurementArea";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";

/**
 * 면적 측정을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for area measurement using mouse/keyboard input.
 * @alias InteractionArea
 * @constructor
 *
 * @param {object} areaOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [areaOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [areaOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [areaOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. Callback function executed when creating last point.
 * @param {MeasurementArea}  [areaOption.measurementArea] 면적 측정 및 영역 표시 클래스.</br> area calculation and area display class.
 *
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Area Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const measurementArea = new Ditap.MeasurementArea(viewer);
 *
 * // InteractionArea on/off 실행 시 MeasurementArea 초기화 필수
 * // MeasurementArea 초기화 방법
 * // 1. InteractionArea 생성 후, MeasurementArea와 callback 함수 대입
 * const interactionArea = new Ditap.InteractionArea();
 * interactionArea.measurementArea = measurementArea;
 * interactionArea.startCallback = () => {};
 * interactionArea.addCallback = () => {};
 * interactionArea.endCallback = () => {};
 *
 * // 2. InteractionArea 생성 시, areaOption 지정
 * const areaOption = {
 *    measurementArea: measurementArea,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 * const interactionArea = new Ditap.InteractionArea(areaOption);
 */
class InteractionArea {
  constructor(areaOption) {
    this._measurementArea = undefined;
    if (areaOption && areaOption.measurementArea instanceof MeasurementArea) {
      this.measurementArea = areaOption.measurementArea; //원본 클래스
    }
    this._handler = undefined;
    this._isOn = false;
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._keyDownEvent = undefined;
    this._createWarningAlert = undefined;
    this._drawingPointEntities = [];
    this._buttonElement = undefined;
    this.drawingPositions = []; // 영역 지정 좌표 목록
    this.drawingPolylineEntity = undefined; // 영역 지정 폴리라인
    this.drawingPolygonEntity = undefined; // 영역 지정 폴리곤
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    if (areaOption) {
      if (areaOption.startCallback instanceof Function) {
        this._startCallback = areaOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (areaOption.addCallback instanceof Function) {
        this._addCallback = areaOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (areaOption.endCallback instanceof Function) {
        this._endCallback = areaOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 면적 계산 및 영역 표시 클래스.</br>
   * Area calculation and area display class.
   * @type {MeasurementArea}
   */
  set measurementArea(measurementArea) {
    if (
      measurementArea instanceof MeasurementArea ||
      measurementArea === undefined
    ) {
      this._measurementArea = measurementArea;
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get measurementArea() {
    return this._measurementArea;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(isOn) {
    this._isOn = isOn;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert.
   * @type {Element}
   */
  get createWarningAlert() {
    return this._createWarningAlert;
  }
  set createWarningAlert(createWarningAlert) {
    this._createWarningAlert = createWarningAlert;
  }

  /*
   * 영역 지정 중인 폴리라인 엔티티. </br>
   * Volume draw polyline entity.
   * @type {Entity}
   */
  get drawingPolylineEntity() {
    return this._drawingPolylineEntity;
  }
  set drawingPolylineEntity(drawingPolylineEntity) {
    this._drawingPolylineEntity = drawingPolylineEntity;
  }

  /*
   * 영역 지정 중인 폴리곤 엔티티. </br>
   * Volume draw polygon entity.
   * @type {Entity}
   */
  get drawingPolygonEntity() {
    return this._drawingPolygonEntity;
  }
  set drawingPolygonEntity(drawingPolygonEntity) {
    this._drawingPolygonEntity = drawingPolygonEntity;
  }

  /*
   * 마우스 무브 시 생성된 동적 포인트 배열.</br>
   * Dynamic point generated on mouse move.
   * @type {Entity[]}
   */
  set drawingPointEntities(drawingPointEntities) {
    this._drawingPointEntities = drawingPointEntities;
  }
  get drawingPointEntities() {
    return this._drawingPointEntities;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point.
   * @type {Function}
   */
  set startCallback(startCallback) {
    this._startCallback = startCallback;
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point.
   * @type {Function}
   */
  set addCallback(addCallback) {
    this._addCallback = addCallback;
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point.
   * @type {Function}
   */
  set endCallback(endCallback) {
    this._endCallback = endCallback;
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 면적 측정 핸들러 실행. (MeasurementArea 초기화 필수.)</br>
   * Execute area measurement handler.(MeasurementArea initialization is required.)
   */
  on() {
    if (this.measurementArea && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();

      this.measurementArea.viewer.currentEvent = this;
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      const viewer = this.measurementArea.viewer;

      this.handler = new ScreenSpaceEventHandler(viewer.canvas);

      this.isOn = true;
      this.drawingPositions = [];

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.measurementArea.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 ",
        "면적을 측정할 선을 그려주세요",
        true,
        true
      );

      // LABEL - 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.measurementArea.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 면적을 측정할 선을",
        "그려주세요",
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 영역 지정 폴리라인 생성
      const polyline = Utils.createPolyline({
        viewer: viewer,
        positions: new CallbackProperty(() => {
          let arr;
          if (!this._isMobile) {
            let middle = this.mousePosition;
            if (this.drawingPositions.length > 0) {
              const left = this.drawingPositions[
                this.drawingPositions.length - 1
              ];
              const right = this.mousePosition;
              middle = Cartesian3.lerp(left, right, 9 / 10, new Cartesian3());
            }
            arr = [...this.drawingPositions, middle];
          } else {
            arr = [...this.drawingPositions];
          }
          if (arr.length > 1) {
            return arr;
          }
        }, false),
      });
      this.drawingPolylineEntity = polyline;

      // 영역 지정 폴리곤 생성
      const polygon = Utils.createPolygon({
        viewer: viewer,
        positions: new CallbackProperty(() => {
          // PolygonHierarchy 변환 과정에 높이가 0로 바뀜 => 원본 Cartesian3 복사본 넘겨주기
          const arr = [];
          for (const drawingPosition of this.drawingPositions) {
            arr.push(Cartesian3.clone(drawingPosition, new Cartesian3()));
          }

          if (!this._isMobile) {
            arr.push(Cartesian3.clone(this.mousePosition, new Cartesian3()));
          }
          if (arr.length > 2) {
            const positions = new PolygonHierarchy(arr);
            return positions;
          }
        }, false),
      });
      this.drawingPolygonEntity = polygon;

      // // POLYLINE - 동적 엔티티 생성
      // const polylineEntity = Utils.createPolyline({
      //   viewer: viewer,
      //   positions: new CallbackProperty(() => {
      //     if (this.measurementArea.positions.length > 0) {
      //       const positions = this.measurementArea.positions;
      //       const array = [];
      //       array.push(positions[positions.length - 1]);
      //       array.push(this.mousePosition);
      //       return array;
      //     }
      //   }, false),
      // });
      // this.moveEntities.push(polylineEntity);

      // // POLYGON - 동적 엔티티 생성
      // const polygonEntity = Utils.createPolygon({
      //   viewer: viewer,
      //   positions: new CallbackProperty(() => {
      //     if (this.measurementArea.positions.length > 1) {
      //       const newPolygon = JSON.parse(
      //         JSON.stringify(this.measurementArea.positions)
      //       );
      //       newPolygon.push(this.mousePosition);
      //       const polygon = new PolygonHierarchy(newPolygon);
      //       return polygon;
      //     }
      //   }, false),
      // });
      // this.moveEntities.push(polygonEntity);

      // 핸들러 - 실행
      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();

      // 경고창 생성
      this.createWarningAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "면적 측정 시, ",
        content2: "세 개 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });
    }
  }

  /**
   * 면적 측정 핸들러 종료. (MeasurementArea 초기화 필수.)</br></br>
   * End area measurement handler.(MeasurementArea initialization is required.)
   */
  off() {
    if (this.measurementArea && this.isOn) {
      const viewer = this.measurementArea.viewer;

      this.measurementArea.viewer.currentEvent = undefined;
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      viewer.entities.remove(this.drawingPolylineEntity);
      viewer.entities.remove(this.drawingPolygonEntity);

      // POINT 삭제
      this.drawingPointEntities.forEach((pointEntity) => {
        viewer.entities.remove(pointEntity);
      });

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      }
      window.removeEventListener("keydown", this.keyDownEvent);

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }
      // window.removeEventListener("mousemove", this.mousemoveEvent);
      // this.removeChangeEvent();

      // 프로퍼티 초기화
      this.handler = undefined;
      this.drawingPositions = [];
      this.isOn = false;
      this.mousePosition = undefined;
      this.keyDownEvent = undefined;
      this.drawingPointEntities = [];
      this.drawingPolylineEntity = undefined;
      this.drawingPolygonEntity = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;

      // alert창 삭제
      this.createWarningAlert.destroy();
      this.createWarningAlert = undefined;
      this.measurementArea = undefined;
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }

      // this.changeLengthLabelPosition();
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    // LEFT_CLICK 핸들러
    handler.setInputAction((e) => {
      if (this.isOn) {
        const allDynamicEntity = [
          ...this.drawingPointEntities,
          this.drawingPolygonEntity,
          this.drawingPolylineEntity,
        ];

        allDynamicEntity.forEach((entities) => {
          entities.show = false;
        });

        setTimeout(() => {
          const position = Utils.pickPosition(
            e.position,
            this.measurementArea.viewer
          );

          if (defined(position)) {
            const viewer = this.measurementArea.viewer;
            let isEqual = false;
            this.mousePosition = position;

            // 마지막으로 추가된 좌표와 현재 좌표 비교
            const drawingPositionsLength = this.drawingPositions.length;
            if (drawingPositionsLength > 0) {
              const lastPosition = this.drawingPositions[
                drawingPositionsLength - 1
              ];
              if (lastPosition.equals(position)) {
                isEqual = true;
              }
            }

            // 영역 지정 포인트 생성
            if (!isEqual && defined(position)) {
              this.measurementArea.positions.push(position);
              const drawingPoint = Utils.createPoint({
                viewer: viewer,
                position: position,
              });
              this.drawingPointEntities.push(drawingPoint);

              // 영역 지정중인 좌표 추가
              this.drawingPositions.push(position);

              // 점 생성 시 콜백함수 실행
              if (this.addCallback && this.measurementArea) {
                this.addCallback(this.measurementArea);
              }

              if (
                this.drawingPositions.length === 1 &&
                this.startCallback &&
                this.measurementArea
              ) {
                this.startCallback(this.measurementArea);
              }
            }
          }
          allDynamicEntity.forEach((entities) => {
            entities.show = true;
          });
        }, 5);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.measurementArea.viewer;
    const handler = this.handler;
    handler.setInputAction((e) => {
      if (this.isOn) {
        const position = Utils.pickPosition(e.endPosition, viewer);
        if (defined(position)) {
          this.mousePosition = position;

          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(
            viewer,
            this.mousePosition,
            this.infoLabelEntity
          );

          if (this.polylinePrimitive) {
            viewer.scene.primitives.remove(this.polylinePrimitive);
          }

          const positions = this.drawingPositions;

          if (positions.length > 0) {
            const array = [];
            array.push(positions[positions.length - 1]);
            array.push(position);
            const polyline = Utils.createPolylinePrimitive({
              viewer: viewer,
              positions: array,
            });
            this.polylinePrimitive = polyline;
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 더블 클릭 이벤트 핸들러 생성.</br>
   * Create mouse double-click event handler.
   */
  createDoubleClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 면생성 종료.<br>
   * End straight line distance measurement.
   */
  endHandler() {
    const positions = this.measurementArea.positions;
    if (this.isOn) {
      if (positions.length > 2) {
        setTimeout(() => {
          this.measurementArea.createEntity();

          // 마지막 점 생성 시 콜백함수 실행
          if (this.endCallback && this.measurementArea) {
            this.endCallback(this.measurementArea);
          }

          this.off();
        }, 6);
      } else {
        this.createWarningAlert.open();
      }
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    // const viewer = this.measurementArea.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (0 < this.drawingPositions.length) {
        this.removePoint();
      } else {
        this.off();
        this.abort();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    const viewer = this.measurementArea.viewer;
    // 마지막 좌표 제거
    // 영역 지정 마지막 좌표 제거
    if (this.polylinePrimitive) {
      viewer.scene.primitives.remove(this.polylinePrimitive);
    }

    const drawingPositionsLength = this.drawingPositions.length;
    if (drawingPositionsLength > 0) {
      // 배열 마지막 아이템 제거
      this.drawingPositions = this.drawingPositions.slice(
        0,
        drawingPositionsLength - 1
      );

      this.measurementArea.positions.pop();
    }
    const drawingPointEntityLength = this.drawingPointEntities.length;
    if (drawingPointEntityLength > 0) {
      const lastPointEntity = this.drawingPointEntities[
        drawingPointEntityLength - 1
      ];
      // 엔티티 제거
      viewer.entities.remove(lastPointEntity);
      // 배열 마지막 아이템 제거
      this.drawingPointEntities = this.drawingPointEntities.slice(
        0,
        drawingPointEntityLength - 1
      );
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /**
   * 측정 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    // measurementArea 삭제
    this.measurementArea?.destroy();
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료

      this.off();
      this.abort();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }
}

export default InteractionArea;
